import { Skeleton } from "@patient-watch/ui/skeleton";
import {
    findBranchingScaleSelection,
    parseBranchingScaleSelectionText,
} from "@/components/input/custom/branching-scale-options/parseBranchingScaleOptions";
import { useSelectInputById } from "@/features/inputs/api/selectInputById";
import { cn } from "@/lib/utils";
import { BaseEntryValue } from "./BaseEntryValue";
import type { BaseEntryValueProps } from "./BaseEntryValue";

export interface BranchingScaleOptionsEntryValueProps extends Omit<BaseEntryValueProps, "value"> {
    value: number | null;
    textValue?: string | null;
    inputId: string;
}

export function BranchingScaleOptionsEntryValue({
    value,
    textValue,
    inputId,
    inputLabel,
    size,
    ...props
}: BranchingScaleOptionsEntryValueProps) {
    const { data: input, isLoading } = useSelectInputById(inputId);

    if (isLoading) {
        return <Skeleton className={cn("w-full h-24", size === "sm" && "h-16", size === "lg" && "h-28")} />;
    }

    const parsedText = parseBranchingScaleSelectionText(textValue);
    const selection = findBranchingScaleSelection(input?.scale_options, value, parsedText?.branchLabel);

    if (value === null) {
        return (
            <BaseEntryValue inputLabel={inputLabel} size={size} {...props}>
                <span
                    className={cn(
                        "text-slate-900 dark:text-slate-200",
                        size === "sm" ? "text-sm" : size === "lg" ? "text-lg" : "text-md"
                    )}
                >
                    None
                </span>
            </BaseEntryValue>
        );
    }

    const branchLabel = selection?.branchLabel ?? parsedText?.branchLabel;
    const optionLabel = selection?.optionLabel ?? parsedText?.optionLabel;
    const branchText = selection?.branchText ?? parsedText?.branchText;
    const compact = size === "sm";

    return (
        <BaseEntryValue inputLabel={inputLabel} size={size} {...props}>
            <div className={cn("space-y-3 pt-1", size === "lg" && "space-y-4")}>
                <div className="flex items-start gap-3">
                    <span
                        className={cn(
                            "inline-flex justify-center items-center bg-indigo-600 rounded-full font-semibold text-white shrink-0",
                            compact ? "w-8 h-8 text-sm" : "w-10 h-10 text-lg"
                        )}
                    >
                        {branchLabel || "?"}
                    </span>
                    <div className={cn("min-w-0", compact ? "pt-1" : "pt-1.5")}>
                        <p
                            className={cn(
                                "font-medium text-slate-900 dark:text-slate-100 leading-snug",
                                compact ? "text-sm" : size === "lg" ? "text-lg" : "text-base"
                            )}
                        >
                            {branchText || (branchLabel ? `Option ${branchLabel}` : "Selected option")}
                        </p>
                    </div>
                </div>

                <div
                    className={cn(
                        "flex justify-between items-center gap-4 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-lg",
                        compact ? "ml-0 px-2.5 py-2" : "ml-[52px] px-3 py-2.5"
                    )}
                >
                    <p
                        className={cn(
                            "min-w-0 font-medium text-slate-800 dark:text-slate-100 leading-snug",
                            compact ? "text-sm" : size === "lg" ? "text-lg" : "text-base"
                        )}
                    >
                        {optionLabel || "Duration not recorded"}
                    </p>
                    <div className="text-right shrink-0">
                        <p
                            className={cn(
                                "font-semibold tabular-nums text-slate-900 dark:text-white leading-none",
                                compact ? "text-lg" : size === "lg" ? "text-2xl" : "text-xl"
                            )}
                        >
                            {value}
                        </p>
                        <p className={cn("mt-0.5 text-slate-500", compact ? "text-[10px]" : "text-xs")}>points</p>
                    </div>
                </div>
            </div>
        </BaseEntryValue>
    );
}
